/**
 * constants.ts
 * =============================================================================
 * Global constants for Neuro-Audio Lab application.
 * 
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

// =============================================================================
// APPLICATION INFO
// =============================================================================

export const APP_NAME = 'Neuro-Audio Lab';
export const APP_VERSION = 'v0.9.0-beta';
export const APP_STUDIO = 'Polígono Studio';
export const APP_COUNTRY = 'Ecuador';
export const APP_YEAR = '2026';
export const APP_DESCRIPTION = 'Herramientas de arquitectura cognitiva basadas en psicoacústica';

// =============================================================================
// AUDIO SETTINGS
// =============================================================================

/**
 * Default sample rate for AudioContext
 * 44100Hz is CD quality and widely supported
 */
export const DEFAULT_SAMPLE_RATE = 44100;

/**
 * Minimum and maximum volume values
 */
export const VOLUME_MIN = 0;
export const VOLUME_MAX = 1;

/**
 * Default ramp time for volume changes (in seconds)
 * Prevents clicks and pops during volume adjustments
 */
export const DEFAULT_RAMP_TIME = 0.1;

/**
 * Fade out duration for sleep mode (in seconds)
 */
export const SLEEP_FADE_OUT_DURATION = 300; // 5 minutes

/**
 * Supported audio formats in order of preference
 */
export const SUPPORTED_AUDIO_FORMATS = ['ogg', 'mp3', 'wav'] as const;

// =============================================================================
// BINAURAL FREQUENCIES
// =============================================================================

/**
 * Brainwave frequency ranges (in Hz)
 */
export const BRAINWAVE_FREQUENCIES = {
  DELTA: { min: 0.5, max: 4, description: 'Sueño profundo' },
  THETA: { min: 4, max: 8, description: 'Meditación, creatividad' },
  ALPHA: { min: 8, max: 12, description: 'Relajación despierta' },
  BETA: { min: 12, max: 30, description: 'Concentración activa' },
  GAMMA: { min: 30, max: 100, description: 'Alta cognición' },
} as const;

/**
 * Carrier frequency range for binaural beats
 * 100-400Hz is optimal for clear beat perception
 */
export const CARRIER_FREQUENCY_MIN = 100;
export const CARRIER_FREQUENCY_MAX = 400;

// =============================================================================
// TIMER SETTINGS
// =============================================================================

/**
 * Timer duration presets (in minutes)
 */
export const TIMER_DURATIONS = {
  POMODORO: 25,
  SHORT_BREAK: 5,
  LONG_BREAK: 15,
  ULTRADIAN: 90,
  HOUR: 60,
  HALF_HOUR: 30,
} as const;

/**
 * Minimum custom timer duration (in minutes)
 */
export const TIMER_MIN_DURATION = 1;

/**
 * Maximum custom timer duration (in minutes)
 */
export const TIMER_MAX_DURATION = 480; // 8 hours

// =============================================================================
// UI SETTINGS
// =============================================================================

/**
 * Debounce delay for volume slider changes (in ms)
 */
export const VOLUME_SLIDER_DEBOUNCE = 50;

/**
 * Animation duration for UI transitions (in ms)
 */
export const UI_TRANSITION_DURATION = 200;

/**
 * Tooltip delay (in ms)
 */
export const TOOLTIP_DELAY = 500;

// =============================================================================
// STORAGE KEYS
// =============================================================================

/**
 * LocalStorage keys for persisted data
 */
export const STORAGE_KEYS = {
  PLAYER_STATE: 'neuro-audio-player',
  USER_PREFERENCES: 'neuro-audio-preferences',
  SESSION_HISTORY: 'neuro-audio-sessions',
} as const;

// =============================================================================
// API ENDPOINTS (Future use)
// =============================================================================

/**
 * Base URL for API calls (when backend is implemented)
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Audio assets base URL
 */
export const AUDIO_BASE_URL = import.meta.env.VITE_AUDIO_URL || '/audio';

// =============================================================================
// FEATURE FLAGS
// =============================================================================

/**
 * Feature flags for progressive rollout
 */
export const FEATURES = {
  ENABLE_VISUALIZER: true,
  ENABLE_VOICE_GUIDES: true,
  ENABLE_SESSION_TRACKING: true,
  ENABLE_BILATERAL_PANNING: false, // Phase 2
  ENABLE_CUSTOM_PRESETS: false, // Phase 2
} as const;

// =============================================================================
// ERROR MESSAGES
// =============================================================================

/**
 * User-friendly error messages (Spanish)
 */
export const ERROR_MESSAGES = {
  INIT_FAILED: 'No se pudo inicializar el motor de audio. Por favor, recarga la página.',
  LOAD_FAILED: 'Error al cargar el archivo de audio. Verifica tu conexión.',
  PLAYBACK_FAILED: 'Error durante la reproducción. Intenta de nuevo.',
  CONTEXT_SUSPENDED: 'Haz clic en cualquier lugar para activar el audio.',
  UNSUPPORTED_BROWSER: 'Tu navegador no soporta Web Audio API. Prueba con Chrome o Firefox.',
} as const;

// =============================================================================
// ANALYTICS EVENTS (Future use)
// =============================================================================

/**
 * Event names for analytics tracking
 */
export const ANALYTICS_EVENTS = {
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
  MODE_SELECTED: 'mode_selected',
  VOLUME_CHANGED: 'volume_changed',
  TIMER_COMPLETED: 'timer_completed',
} as const;